import { Link } from "react-router-dom";

const TermsAndConditions = () => {
  return (
    <div className="bg-gray-50 text-gray-800 px-4 py-12 min-h-screen">
      <div className="max-w-4xl mx-auto bg-white p-8 rounded-lg shadow-md">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Terms of Use (EULA) &amp; Terms of Service
        </h1>
        <p className="text-sm text-gray-500 mb-6">
          Last updated: August 24, 2026
        </p>

        <p className="mb-4">
          These <strong>Terms of Use (End User License Agreement / EULA)</strong>{" "}
          and <strong>Terms of Service</strong> govern your use of{" "}
          <strong>It&apos;s All About The Options</strong> (&quot;IAATO,&quot;
          &quot;we,&quot; &quot;our,&quot; or &quot;us&quot;), including our
          website, mobile applications, memberships, and related Services. By
          accessing or using the Services, you agree to these terms. If you do
          not agree, do not use the Services.
        </p>

        <p className="mb-4">
          Website:{" "}
          <a
            href="https://itsallaboutheoptions.com"
            className="text-indigo-600 hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://itsallaboutheoptions.com
          </a>
          . Privacy practices are described in our{" "}
          <Link to="/privacy-policy" className="text-indigo-600 hover:underline">
            Privacy Policy
          </Link>
          .
        </p>

        {/* ========== EULA ========== */}
        <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 border-b border-gray-200 pb-2">
          Terms of Use (EULA)
        </h2>

        <p className="mb-4">
          This End User License Agreement (&quot;EULA&quot; or &quot;Terms of
          Use&quot;) governs your use of the It&apos;s All About The Options
          mobile application(s) and related Services. By downloading,
          installing, or using the app, you agree to this EULA.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">1. License Grant</h3>
        <p className="mb-4">
          Subject to your compliance with this EULA, we grant you a limited,
          non-exclusive, non-transferable, revocable license to use the app on
          devices you own or control, solely for your personal, non-commercial
          use, in accordance with the Apple App Store Terms of Service (where
          applicable) and these Terms.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">2. Age Requirement</h3>
        <p className="mb-4">
          You must be at least 18 years of age to use the Services. If you are
          not at least 18 years old, you may not access or use the Services.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">
          3. Auto-Renewable Subscriptions
        </h3>
        <p className="mb-4">
          The app may offer auto-renewable subscriptions under the subscription
          group <strong>IAATO Membership</strong>, including:
        </p>
        <ul className="list-disc list-inside mb-4">
          <li>
            <strong>Monthly Subscription</strong> — billed every 1 month
          </li>
          <li>
            <strong>Annually Subscription</strong> — billed every 1 year
          </li>
        </ul>
        <p className="mb-4">
          Current pricing is displayed in the app and on the App Store product
          page at the time of purchase and may vary by region.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">
          4. Payment &amp; Renewal (Apple App Store)
        </h3>
        <ul className="list-disc list-inside mb-4 space-y-2">
          <li>
            Payment will be charged to your Apple ID account at confirmation of
            purchase.
          </li>
          <li>
            Subscriptions automatically renew unless auto-renew is turned off at
            least 24 hours before the end of the current period.
          </li>
          <li>
            Your account will be charged for renewal within 24 hours prior to
            the end of the current period at the rate of the selected plan.
          </li>
          <li>
            You can manage or cancel your subscription in your Apple ID Account
            Settings after purchase. On iOS: Settings → [Your Name] →
            Subscriptions.
          </li>
          <li>
            Any unused portion of a free trial period, if offered, will be
            forfeited when you purchase a subscription to that publication,
            where applicable.
          </li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-2">
          5. Cancellation &amp; Refunds (App Store)
        </h3>
        <p className="mb-4">
          You may cancel an App Store subscription at any time through your
          Apple ID subscription settings. Cancellation takes effect at the end
          of the current billing period. Refunds for App Store purchases are
          handled by Apple in accordance with Apple&apos;s refund policies.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">
          6. Educational Disclaimer
        </h3>
        <p className="mb-4">
          The Services are for general informational and educational purposes
          only. None of the Services is intended as investment, tax, accounting,
          or legal advice, as an offer or solicitation to buy or sell
          securities, or as an endorsement of any company, security, or fund.
          You are solely responsible for your own investment research and
          decisions.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">7. Restrictions</h3>
        <p className="mb-2">You agree not to:</p>
        <ul className="list-disc list-inside mb-4">
          <li>Copy, modify, distribute, or reverse engineer the app</li>
          <li>Share your account credentials with others</li>
          <li>
            Use the Services for any unlawful purpose or in violation of these
            Terms
          </li>
          <li>
            Attempt to interfere with the security or proper working of the
            Services
          </li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-2">
          8. Intellectual Property
        </h3>
        <p className="mb-4">
          All content, trademarks, logos, and materials in the Services are owned
          by It&apos;s All About The Options or its licensors and are protected
          by intellectual property laws. No rights are granted except as
          expressly set forth in this EULA.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">9. Termination</h3>
        <p className="mb-4">
          We may suspend or terminate your access to the Services if you violate
          this EULA or for any other reason at our discretion. Upon termination,
          the license granted herein ends immediately, and you must stop using
          the app.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">
          10. Limitation of Liability
        </h3>
        <p className="mb-4">
          To the maximum extent permitted by law, It&apos;s All About The Options
          and its affiliates shall not be liable for any indirect, incidental,
          special, consequential, or punitive damages, or any loss of profits or
          data, arising from your use of the Services. Your sole remedy for
          dissatisfaction with the Services is to stop using them. Maximum
          liability shall not exceed the total amount paid by you, if any, to
          access the Services.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">
          11. Changes to These Terms
        </h3>
        <p className="mb-4">
          We may update this EULA from time to time. Material changes will be
          reflected on this page with an updated &quot;Last updated&quot; date.
          Continued use of the Services after changes constitutes acceptance of
          the revised Terms.
        </p>

        {/* ========== WEBSITE / MEMBERSHIP TERMS ========== */}
        <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 border-b border-gray-200 pb-2">
          Website &amp; Membership Terms of Service
        </h2>

        <p className="mb-4">
          The following terms apply to website memberships and purchases made
          outside the Apple App Store (for example, via our website, Stripe, or
          PayPal). For App Store purchases, Apple&apos;s rules and the EULA
          section above control.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">Services</h3>
        <p className="mb-4">
          <strong>IT&apos;S ALL ABOUT THE OPTIONS</strong> provides the website
          known as{" "}
          <a
            href="https://itsallaboutheoptions.com"
            className="text-indigo-600 hover:underline"
          >
            itsallaboutheoptions.com
          </a>{" "}
          and related information, including email newsletters, chats, text
          alerts, courses, and membership content (collectively, the
          &quot;Services&quot;).
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">
          Changes to Terms and Services
        </h3>
        <ul className="list-disc list-inside mb-4">
          <li>Change the terms and conditions of this Agreement</li>
          <li>
            Change the Services, including eliminating or discontinuing any
            content or feature
          </li>
          <li>Change any fees or charges for use of the Services</li>
        </ul>
        <p className="mb-4">
          Changes are effective seven (7) days after notice is provided (including
          by posting on the Services or by email). Continued use after notice
          constitutes acceptance.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">
          Website Refund &amp; Cancellation Policy
        </h3>
        <p className="mb-4">
          Cancellation for website billing takes effect no later than two (2)
          calendar days after we receive your notification via email to{" "}
          <a
            href="mailto:info@itsallaboutheoptions.com"
            className="text-indigo-600 hover:underline"
          >
            info@itsallaboutheoptions.com
          </a>{" "}
          or via the applicable payment provider cancellation flow.
        </p>
        <ul className="list-disc list-inside mb-4 space-y-2">
          <li>
            Stopping or pausing text alerts does not constitute cancellation of
            Services.
          </li>
          <li>
            Where a free trial is offered for website subscriptions, your card
            will not be charged until after the trial ends, and only if you do
            not cancel during the trial. Only one free trial is allowed per
            person or address, unless otherwise stated.
          </li>
          <li>
            <strong>Monthly:</strong> After any trial, you may cancel at the end
            of any monthly period without further obligation. There are no
            refunds on months already used.
          </li>
          <li>
            <strong>Annual:</strong> After any trial, you may cancel during the
            31st–60th day of your annual subscription and may continue until the
            60th day. You will be refunded the full amount within 25 calendar
            days, minus $100. No refunds after 61 calendar days.
          </li>
          <li>
            Returning members may cancel with a full refund during their first 3
            calendar days of any monthly website subscription.
          </li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-2">Code of Conduct</h3>
        <p className="mb-2">While using the Services you agree not to:</p>
        <ul className="list-disc list-inside mb-4">
          <li>
            Restrict or inhibit any other member from using the Services
            (including hacking, cracking, or defacing any portion of the
            Services)
          </li>
          <li>Use the Services for any unlawful purpose</li>
          <li>
            Transmit unlawful, fraudulent, threatening, harassing, abusive,
            libelous, defamatory, or obscene content
          </li>
          <li>
            Transmit advertisements, spam, or unsolicited commercial
            communications without our permission
          </li>
          <li>
            Transmit software or materials containing viruses or other harmful
            components
          </li>
          <li>
            Modify, reverse engineer, or remove proprietary notices from the
            Services
          </li>
          <li>
            Harvest or collect information about visitors or members without
            their consent
          </li>
          <li>
            Permit anyone whose account was terminated to use the Services
            through your account
          </li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-2">Ownership</h3>
        <p className="mb-4">
          You may access materials on the Services for personal use only. You
          may not republish, upload, post, transmit, or distribute materials
          without our prior written permission. You do not acquire ownership
          rights by using the Services.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">Indemnification</h3>
        <p className="mb-4">
          You agree to indemnify, defend, and hold harmless us and our officers,
          directors, employees, and agents from claims, damages, losses, and
          expenses (including reasonable attorneys&apos; fees) arising out of
          your breach of these Terms, content you submit, or your use of the
          Services.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">Arbitration</h3>
        <p className="mb-4">
          Any controversy or claim arising out of or relating to this Agreement
          shall be settled by arbitration administered by the American
          Arbitration Association under its Commercial Arbitration Rules.
          Arbitration shall take place in Denver County, Colorado, USA before
          one arbitrator. Judgment on the award may be entered in any court with
          jurisdiction. The prevailing party is entitled to reasonable
          attorneys&apos; fees and costs.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">Governing Law</h3>
        <p className="mb-4">
          This Agreement is governed by the laws of the State of Colorado,
          United States of America, without regard to conflicts of law
          principles. If any provision is found unenforceable, the remainder
          remains in effect. This Agreement, together with our Privacy Policy,
          constitutes the entire agreement between you and It&apos;s All About
          The Options regarding the Services.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">
          Investment Disclaimer
        </h3>
        <p className="mb-4">
          DO NOT BASE ANY INVESTMENT DECISION SOLELY UPON MATERIALS FOUND ON
          THIS WEBSITE, SMS, APP, OR TRADING CHAT IDEAS. We are not registered
          with the SEC or any state securities authority and are not licensed to
          provide investment advice. Content is impersonalized and offered for
          general informational purposes only. Investing is speculative and
          high-risk. Always do your own research and consult a registered
          financial advisor. For verified information, see{" "}
          <a
            href="https://www.sec.gov"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 hover:underline"
          >
            www.sec.gov
          </a>{" "}
          and{" "}
          <a
            href="https://www.finra.org"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 hover:underline"
          >
            www.finra.org
          </a>
          .
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-2">Contact Us</h3>
        <p className="mb-2">
          Questions about these Terms of Use (EULA) or Terms of Service:
        </p>
        <ul className="list-none mb-4 space-y-1">
          <li>
            Email:{" "}
            <a
              href="mailto:info@itsallaboutheoptions.com"
              className="text-indigo-600 hover:underline"
            >
              info@itsallaboutheoptions.com
            </a>
          </li>
          <li>
            Email:{" "}
            <a
              href="mailto:contact@itsallaboutheoptions.com"
              className="text-indigo-600 hover:underline"
            >
              contact@itsallaboutheoptions.com
            </a>
          </li>
          <li>
            <Link
              to="/#contact"
              className="font-medium text-indigo-600 hover:underline"
            >
              Contact form
            </Link>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default TermsAndConditions;
